import { useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import {
    setVirtualBackgroundLocalImageUrl,
    setVirtualBackgroundImages,
    setSelectedVirtualBackgroundImageId,
    setIsRemoveVirtualBackgroundUploadImage,
} from "../../reducers/call-setting-reducer";
import { VIRTUAL_BACKGROUND } from "../../constants/VirtualBackgroundConstant";
import storage from "../../services/storage";
import { LOCAL_STORAGE_KEYS } from "../../constants/Constants";
import CustomToast from "../UI/customToast";
import RemoveVirtualBackgroundUploadImageModal from "./RemoveVirtualBackgroundUploadImageModal";
import { addDragHoverClass, removeDragHoverClass, stopPropagationAndPreventEvent } from "../CommonModals/CommonUtils";

const VirtualBackgroundUploadImage = ({ localisedValues }) => {
    const dispatch = useDispatch();
    const virtualBackgroundFileInputRef = useRef(null);
    const uploadVirtualBackgroundImageDragDropDivRef = useRef();

    const { virtualBackgroundImages, virtualBackgroundLocalImageUrl, isRemoveVirtualBackgroundUploadImage } = useSelector(
        (state) => state.callSetting
    );

    const handleVBUploadClick = () => {
        virtualBackgroundFileInputRef.current.click();
    };

    const handleVirtualBackgroundFileChange = (event, fromWhere = "") => {
        let files = [];

        if (fromWhere === VIRTUAL_BACKGROUND.FROM_DRAG_IMAGE) {
            // Drag-and-drop: event can be a File or an array of Files
            files = event;
        } else {
            // File input click: allow only one file
            const file = event.target?.files?.[0];
            if (!file) return;
            files = [file];
        }

        // Find the first supported image file
        const supportedFile = files?.find((file) => {
            const ext = file.name?.toLowerCase()?.split(".")?.pop();
            return VIRTUAL_BACKGROUND.ALLOWD_IMAGE_EXTENSIONS.includes(ext);
        });

        if (!supportedFile) {
            CustomToast.error(localisedValues?.unsupported_file_format);
            return;
        }

        if (supportedFile?.size > VIRTUAL_BACKGROUND.MAX_SIZE) {
            CustomToast.error(localisedValues?.the_maximum_file_size_supported_is_in_mb?.replace("<size>", 10));
            return;
        }

        const reader = new FileReader();
        reader.onload = function (e) {
            const img = new Image();
            img.onload = function () {
                // Create canvas of 1920x1080 to enforce size & aspect ratio
                const canvas = document.createElement("canvas");
                const ctx = canvas.getContext("2d");

                canvas.width = VIRTUAL_BACKGROUND.RECOMMENDED_IMAGE_WIDTH_DIMENSION;
                canvas.height = VIRTUAL_BACKGROUND.RECOMMENDED_IMAGE_HEIGHT_DIMENSION;

                // Fill background with white (for PNG transparency)
                ctx.fillStyle = "#ffffff";
                ctx.fillRect(0, 0, canvas.width, canvas.height);

                // Scale image to cover 16:9 canvas while preserving aspect ratio
                const scale = Math.max(canvas.width / img.width, canvas.height / img.height);

                const scaledWidth = img.width * scale;
                const scaledHeight = img.height * scale;

                const x = (canvas.width - scaledWidth) / 2;
                const y = (canvas.height - scaledHeight) / 2;

                ctx.drawImage(img, x, y, scaledWidth, scaledHeight); // draw image on top

                // The image is exported in JPEG format (which doesn't support transparency anyway)
                const dataUrl = canvas.toDataURL("image/jpeg", 0.9); // JPEG quality 0.9
                updateAndSetVirtualBackgroundImagesData(dataUrl);
            };
            img.src = e.target.result;
        };

        reader.readAsDataURL(supportedFile);
    };

    const updateAndSetVirtualBackgroundImagesData = (imageUrl) => {
        storage.set(LOCAL_STORAGE_KEYS.VIRTUAL_BACKGROUND_UPLOAD_IMAGE, imageUrl); // set the image in local storage
        const localUploadImageData = { id: VIRTUAL_BACKGROUND.LOCAL_IMAGE_ID, image: imageUrl };
        const updatedVBImages = virtualBackgroundImages.some((img) => img.id === VIRTUAL_BACKGROUND.LOCAL_IMAGE_ID)
            ? virtualBackgroundImages.map((img) => (img.id === VIRTUAL_BACKGROUND.LOCAL_IMAGE_ID ? localUploadImageData : img))
            : [localUploadImageData, ...virtualBackgroundImages];

        dispatch(setSelectedVirtualBackgroundImageId(VIRTUAL_BACKGROUND.LOCAL_IMAGE_ID));
        storage.set(LOCAL_STORAGE_KEYS.SELECTED_VIRTUAL_BACKGROUND_IMAGE_ID, VIRTUAL_BACKGROUND.LOCAL_IMAGE_ID); // set the selected image id in local storage
        dispatch(setVirtualBackgroundImages(updatedVBImages));
        dispatch(setVirtualBackgroundLocalImageUrl(imageUrl));
    };

    const handleRemoveVBUploadImageClick = () => {
        dispatch(setIsRemoveVirtualBackgroundUploadImage(true));
    };

    const addDragClassInVirtualBackgroundUploadDiv = (e) => {
        stopPropagationAndPreventEvent(e);
        addDragHoverClass(uploadVirtualBackgroundImageDragDropDivRef);
    };

    const removeDragClassInVirtualBackgroundUploadDiv = (e) => {
        stopPropagationAndPreventEvent(e);
        removeDragHoverClass(uploadVirtualBackgroundImageDragDropDivRef);
    };

    const dropHandlerForVirtualBackgroundUploadDiv = (e) => {
        removeDragClassInVirtualBackgroundUploadDiv(e);
        const allFiles = Array.from(e.dataTransfer.files);
        handleVirtualBackgroundFileChange(allFiles, VIRTUAL_BACKGROUND.FROM_DRAG_IMAGE);
    };

    const dragLeaveHandlerForVirtualBackgroundUploadDiv = (e) => {
        removeDragClassInVirtualBackgroundUploadDiv(e);
    };

    const dragEndHandlerForVirtualBackgroundUploadDiv = (e) => {
        removeDragClassInVirtualBackgroundUploadDiv(e);
    };

    const dragOverHandlerForVirtualBackgroundUploadDiv = (e) => {
        addDragClassInVirtualBackgroundUploadDiv(e);
    };

    return (
        <>
            <div className='virtual-background-upload'>
                <div className='title'>{localisedValues?.virtual_background}</div>
                <div className='virtual-bg-upload-area'>
                    <div
                        className='virtual-bg-upload-area-in'
                        ref={uploadVirtualBackgroundImageDragDropDivRef}
                        onDrop={dropHandlerForVirtualBackgroundUploadDiv}
                        onDragLeave={dragLeaveHandlerForVirtualBackgroundUploadDiv}
                        onDragEnd={dragEndHandlerForVirtualBackgroundUploadDiv}
                        onDragOver={dragOverHandlerForVirtualBackgroundUploadDiv}
                    >
                        <label className='virtual-bg-upload-label' htmlFor='virtual-bg-upload'>
                            <input
                                className='virtual-bg-upload-input'
                                id='virtual-bg-upload'
                                ref={virtualBackgroundFileInputRef}
                                onClick={(event) => (event.target.value = null)}
                                type='file'
                                accept='image/jpg, image/jpeg, image/png'
                                onChange={handleVirtualBackgroundFileChange}
                            />
                        </label>
                        {!virtualBackgroundLocalImageUrl ? (
                            <div className='upload-icon'>
                                <span className='for-icon icon-upload-image'></span>
                            </div>
                        ) : (
                            <div className='virtual-bg-uploaded'>
                                <img className='virtual-bg-uploaded-img' src={virtualBackgroundLocalImageUrl} alt='Virtual Background' />
                            </div>
                        )}
                    </div>
                </div>
                <div className='button-inline more-space d-flex align-items-center justify-content-center virtual-bg-action'>
                    <div className='button'>
                        <button className='btn btn-primary' onClick={handleVBUploadClick}>
                            <span className='btn-text'>{localisedValues?.upload_image}</span>
                        </button>
                    </div>
                    <div className='button'>
                        <button
                            className='btn btn-link cst-btn-link'
                            disabled={!virtualBackgroundLocalImageUrl}
                            onClick={handleRemoveVBUploadImageClick}
                        >
                            <span className='btn-text'>{localisedValues?.remove_image}</span>
                        </button>
                    </div>
                </div>
            </div>
            {isRemoveVirtualBackgroundUploadImage && <RemoveVirtualBackgroundUploadImageModal />}
        </>
    );
};

VirtualBackgroundUploadImage.propTypes = {
    localisedValues: PropTypes.any,
};

export default VirtualBackgroundUploadImage;
